<script setup>
import { computed, defineAsyncComponent, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { OnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';

import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import {
  DEFAULT_DASHBOARD_APP_ICON,
  DEFAULT_DASHBOARD_APP_POSITION,
  availableDashboardAppPositionAnchors,
  dashboardAppIconClass,
  normalizeDashboardAppPosition,
} from 'dashboard/routes/dashboard/dashboardApps/sidebarConfiguration';

const props = defineProps({
  show: { type: Boolean, default: false },
  mode: { type: String, default: 'CREATE' },
  selectedAppData: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['close']);
const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);
const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { shouldShow } = usePolicy();
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledOnAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isLoading = ref(false);
const submitted = ref(false);
const isIconPickerOpen = ref(false);
const selectedContent = props.selectedAppData.content?.[0] || {};

const app = reactive({
  title: props.selectedAppData.title || '',
  content: {
    type: 'frame',
    url: '',
    placement: 'conversation_panel',
    access: 'all',
    ...selectedContent,
    icon: selectedContent.icon || DEFAULT_DASHBOARD_APP_ICON,
    position_after: normalizeDashboardAppPosition(
      selectedContent.position_after || DEFAULT_DASHBOARD_APP_POSITION
    ),
  },
});

const sourceOptions = computed(() => [
  {
    value: 'frame',
    label: t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SOURCE_EXTERNAL'),
  },
  {
    value: 'internal_module',
    label: t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SOURCE_INTERNAL'),
  },
]);
const placementOptions = computed(() => [
  {
    value: 'conversation_panel',
    label: t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.PLACEMENT_CONVERSATION'),
  },
  {
    value: 'sidebar',
    label: t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.PLACEMENT_SIDEBAR'),
  },
]);
const accessOptions = computed(() => [
  {
    value: 'all',
    label: t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ACCESS_ALL'),
  },
  {
    value: 'administrator',
    label: t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ACCESS_ADMIN'),
  },
]);
const internalModuleOptions = computed(() => [
  {
    value: 'external_connections',
    label: t(
      'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.MODULE_EXTERNAL_CONNECTIONS'
    ),
  },
]);
const isInternalModule = computed(() => app.content.type === 'internal_module');
const isSidebarApp = computed(() => app.content.placement === 'sidebar');
const availablePositionAnchors = computed(() =>
  availableDashboardAppPositionAnchors({
    routes: router.getRoutes(),
    shouldShow,
    isFeatureEnabled: featureFlag =>
      isFeatureEnabledOnAccount.value(accountId.value, featureFlag),
  })
);
const positionOptions = computed(() => {
  const labels = {
    contacts: t('SIDEBAR.CONTACTS'),
    companies: t('SIDEBAR.COMPANIES'),
    crm: 'CRM',
    reports: t('SIDEBAR.REPORTS'),
    campaigns: t('SIDEBAR.CAMPAIGNS'),
    help_center: t('SIDEBAR.HELP_CENTER.TITLE'),
    cortex: t('SIDEBAR.CORTEX'),
    flowbuilder: t('SIDEBAR.FLOWBUILDER'),
  };

  return availablePositionAnchors.value.map(anchor => ({
    value: anchor.value,
    label: t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.POSITION_OPTION', {
      item: labels[anchor.value],
    }),
  }));
});

watch(
  availablePositionAnchors,
  anchors => {
    if (anchors.some(anchor => anchor.value === app.content.position_after)) {
      return;
    }

    app.content.position_after =
      anchors[0]?.value || DEFAULT_DASHBOARD_APP_POSITION;
  },
  { immediate: true }
);
const iconButtonLabel = computed(() =>
  app.content.icon
    ? t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.CHANGE_ICON')
    : t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SELECT_ICON')
);

const isUpdateMode = computed(() => props.mode === 'UPDATE');
const header = computed(() =>
  isUpdateMode.value
    ? t('INTEGRATION_SETTINGS.DASHBOARD_APPS.UPDATE.HEADER')
    : t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.HEADER')
);
const submitButtonLabel = computed(() =>
  isUpdateMode.value
    ? t('INTEGRATION_SETTINGS.DASHBOARD_APPS.UPDATE.FORM_SUBMIT')
    : t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.FORM_SUBMIT')
);
const isHttpUrl = value => {
  try {
    return ['http:', 'https:'].includes(new URL(value).protocol);
  } catch {
    return false;
  }
};

const titleError = computed(() =>
  submitted.value && !app.title.trim()
    ? t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_ERROR')
    : ''
);
const urlError = computed(() =>
  submitted.value && !isInternalModule.value && !isHttpUrl(app.content.url)
    ? t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_ERROR')
    : ''
);
const isValid = computed(
  () =>
    app.title.trim() &&
    (isInternalModule.value
      ? app.content.module === 'external_connections'
      : isHttpUrl(app.content.url))
);

const onSourceChange = () => {
  if (isInternalModule.value) {
    app.content.module = 'external_connections';
    app.content.placement = 'sidebar';
    app.content.access = 'administrator';
    delete app.content.url;
    return;
  }

  app.content.url = '';
  app.content.placement = 'conversation_panel';
  app.content.access = 'all';
  delete app.content.module;
};

const closeModal = () => emit('close');

const onSelectIcon = ({ value }) => {
  app.content.icon = value;
  isIconPickerOpen.value = false;
};

const submit = async () => {
  submitted.value = true;
  if (!isValid.value) return;

  const action = props.mode.toLowerCase();
  const sidebarConfiguration = isSidebarApp.value
    ? {
        icon: app.content.icon || DEFAULT_DASHBOARD_APP_ICON,
        position_after: normalizeDashboardAppPosition(
          app.content.position_after
        ),
      }
    : {};
  const payload = {
    title: app.title.trim(),
    content: [
      isInternalModule.value
        ? {
            type: 'internal_module',
            module: app.content.module,
            placement: 'sidebar',
            access: 'administrator',
            ...sidebarConfiguration,
          }
        : {
            type: 'frame',
            url: app.content.url.trim(),
            placement: app.content.placement,
            access: app.content.access,
            ...sidebarConfiguration,
          },
    ],
  };
  if (action === 'update') payload.id = props.selectedAppData.id;

  isLoading.value = true;
  try {
    await store.dispatch('dashboardApps/' + action, payload);
    useAlert(
      isUpdateMode.value
        ? t('INTEGRATION_SETTINGS.DASHBOARD_APPS.UPDATE.API_SUCCESS')
        : t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.API_SUCCESS')
    );
    closeModal();
  } catch {
    useAlert(
      isUpdateMode.value
        ? t('INTEGRATION_SETTINGS.DASHBOARD_APPS.UPDATE.API_ERROR')
        : t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.API_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <woot-modal :show="show" :on-close="closeModal">
    <div class="flex max-h-[80vh] flex-col overflow-hidden">
      <woot-modal-header :header-title="header" />
      <form class="flex min-h-0 flex-col" @submit.prevent="submit">
        <div class="flex flex-col gap-4 overflow-y-auto px-1 pb-4">
          <NextInput
            v-model="app.title"
            :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_LABEL')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_PLACEHOLDER')
            "
            :message="titleError"
            :message-type="titleError ? 'error' : 'info'"
            data-testid="app-title"
          />
          <AdaptiveSelect
            v-model="app.content.type"
            :options="sourceOptions"
            :display-label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SOURCE_LABEL')
            "
            :message="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SOURCE_HELP')
            "
            @update:model-value="onSourceChange"
          />
          <AdaptiveSelect
            v-if="isInternalModule"
            v-model="app.content.module"
            :options="internalModuleOptions"
            :display-label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.MODULE_LABEL')
            "
          />
          <NextInput
            v-if="!isInternalModule"
            v-model="app.content.url"
            :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_LABEL')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_PLACEHOLDER')
            "
            :message="urlError"
            :message-type="urlError ? 'error' : 'info'"
            data-testid="app-url"
          />
          <AdaptiveSelect
            v-if="!isInternalModule"
            v-model="app.content.placement"
            :options="placementOptions"
            :display-label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.PLACEMENT_LABEL')
            "
            :message="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.PLACEMENT_HELP')
            "
          />
          <AdaptiveSelect
            v-if="!isInternalModule"
            v-model="app.content.access"
            :options="accessOptions"
            :display-label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ACCESS_LABEL')
            "
          />
          <template v-if="isSidebarApp">
            <WithLabel
              name="dashboard-app-icon"
              :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_LABEL')"
              :help-message="
                $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_HELP')
              "
            >
              <OnClickOutside
                class="relative w-full"
                @trigger="isIconPickerOpen = false"
              >
                <NextButton
                  id="dashboard-app-icon"
                  type="button"
                  faded
                  slate
                  start
                  class="w-full"
                  :icon="dashboardAppIconClass(app.content.icon)"
                  :label="iconButtonLabel"
                  @click="isIconPickerOpen = !isIconPickerOpen"
                >
                  <template #icon>
                    <span
                      :class="dashboardAppIconClass(app.content.icon)"
                      class="flex-shrink-0 size-4 text-n-brand"
                    />
                  </template>
                </NextButton>
                <EmojiIconPicker
                  v-if="isIconPickerOpen"
                  class="left-0 top-11"
                  mode="icon"
                  :value="app.content.icon"
                  :show-color-picker="false"
                  use-brand-color
                  @select="onSelectIcon"
                />
              </OnClickOutside>
            </WithLabel>
            <AdaptiveSelect
              v-model="app.content.position_after"
              :options="positionOptions"
              :display-label="
                $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.POSITION_LABEL')
              "
              :message="
                $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.POSITION_HELP')
              "
            />
          </template>
        </div>

        <div class="flex justify-end gap-2 border-t border-n-weak pt-4">
          <NextButton
            faded
            slate
            type="button"
            icon="i-lucide-x"
            :label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.FORM_CANCEL')
            "
            @click="closeModal"
          />
          <NextButton
            type="submit"
            :icon="mode === 'UPDATE' ? 'i-lucide-refresh-cw' : 'i-lucide-plus'"
            :label="submitButtonLabel"
            :is-loading="isLoading"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
